import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearch } from 'wouter';
import { formatDistanceToNow } from 'date-fns';
import {
  getAdminRequest, listAdminRequests, replyToAdminRequest, setAdminRequestStatus,
  type AdminRequest, type AdminRequestKind, type AdminRequestMessage, type AdminRequestStatus,
} from '@/lib/adminApi';
import { ROLE_LABELS } from '@/lib/adminAreas';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Check, CornerDownLeft, Inbox, Loader2, MessageSquare, Plus, RotateCcw, Undo2, X,
} from 'lucide-react';
import NewRequestDialog, { REQUEST_KINDS, type NewRequestPrefill } from './requests/NewRequestDialog';

const STATUS: Record<AdminRequestStatus, { label: string; className: string }> = {
  open: { label: 'Waiting', className: 'bg-amber-500/10 text-amber-300 border-amber-500/25' },
  approved: { label: 'Approved', className: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/25' },
  declined: { label: 'Declined', className: 'bg-rose-500/10 text-rose-400 border-rose-500/25' },
  answered: { label: 'Answered', className: 'bg-cyan-500/10 text-cyan-300 border-cyan-500/25' },
  withdrawn: { label: 'Withdrawn', className: 'bg-gray-700/50 text-gray-400 border-gray-600' },
};

const STATUS_VERB: Record<AdminRequestStatus, string> = {
  open: 'reopened this request',
  approved: 'approved this request',
  declined: 'declined this request',
  answered: 'marked this as answered',
  withdrawn: 'withdrew this request',
};

const KIND_LABELS: Record<string, string> = Object.fromEntries(REQUEST_KINDS.map((k) => [k.key, k.label]));

function ago(iso: string) {
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

function personLabel(p: { display_name: string; role: string } | null | undefined) {
  if (!p) return 'Someone who has left';
  return `${p.display_name}, ${ROLE_LABELS[p.role] ?? p.role}`;
}

/**
 * Requests to the directors. Staff raise approvals, decisions and questions
 * and see only their own; the owner and directors see everyone's and decide.
 */
export default function Requests() {
  const { isManager } = useAdminAuth();
  const search = useSearch();
  const [view, setView] = useState<'open' | 'closed'>('open');
  const [rows, setRows] = useState<AdminRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ request: AdminRequest; messages: AdminRequestMessage[] } | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [prefill, setPrefill] = useState<NewRequestPrefill | undefined>(undefined);

  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get('id')) setSelectedId(params.get('id'));
    if (params.get('new')) {
      const kind = params.get('kind') as AdminRequestKind | null;
      setPrefill({
        kind: kind && KIND_LABELS[kind] ? kind : undefined,
        subject: params.get('subject') ?? undefined,
        body: params.get('body') ?? undefined,
        assignedTo: params.get('to'),
        pagePath: params.get('path'),
      });
      setNewOpen(true);
    }
    if (params.toString()) window.history.replaceState(null, '', '/admin/requests');
  }, [search]);

  const loadList = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await listAdminRequests(view);
      setRows(res.rows);
    } catch (err: any) {
      if (!quiet) toast.error(err.message || 'Could not load requests');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [view]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const t = setInterval(() => loadList(true), 30000);
    return () => clearInterval(t);
  }, [loadList]);

  const loadDetail = useCallback(async (id: string, quiet = false) => {
    if (!quiet) setDetailLoading(true);
    try {
      const res = await getAdminRequest(id);
      setDetail({ request: res.request, messages: res.messages });
      setRows((prev) => prev.map((r) => (r.id === id ? { ...r, unread: false } : r)));
      window.dispatchEvent(new Event('admin-requests-changed'));
    } catch (err: any) {
      toast.error(err.message || 'Could not open the request');
      setSelectedId(null);
      setDetail(null);
    } finally {
      if (!quiet) setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedId) loadDetail(selectedId);
    else setDetail(null);
    setReply('');
  }, [selectedId, loadDetail]);

  const afterChange = async (id: string) => {
    await Promise.all([loadDetail(id, true), loadList(true)]);
    window.dispatchEvent(new Event('admin-requests-changed'));
  };

  const sendReply = async () => {
    if (!detail || !reply.trim()) return;
    setBusy('reply');
    try {
      await replyToAdminRequest(detail.request.id, reply.trim());
      setReply('');
      await afterChange(detail.request.id);
    } catch (err: any) {
      toast.error(err.message || 'Could not send the reply');
    } finally {
      setBusy(null);
    }
  };

  const changeStatus = async (status: AdminRequestStatus) => {
    if (!detail) return;
    setBusy(status);
    try {
      await setAdminRequestStatus(detail.request.id, status, reply.trim() || undefined);
      setReply('');
      toast.success(STATUS[status].label === 'Waiting' ? 'Request reopened' : `Request ${STATUS[status].label.toLowerCase()}`);
      await afterChange(detail.request.id);
    } catch (err: any) {
      toast.error(err.message || 'Could not update the request');
    } finally {
      setBusy(null);
    }
  };

  const req = detail?.request;
  const canDecide = isManager && req && !req.is_mine;
  const emptyText = useMemo(() => {
    if (view === 'closed') return 'Nothing closed yet.';
    return isManager ? 'No one is waiting on a director right now.' : 'You have no open requests.';
  }, [view, isManager]);

  return (
    <div className="space-y-6">
      <NewRequestDialog
        open={newOpen}
        onOpenChange={setNewOpen}
        prefill={prefill}
        onCreated={(id) => { setView('open'); setSelectedId(id); loadList(true); }}
      />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Requests</h1>
          <p className="text-sm text-gray-400 mt-1 max-w-2xl">
            {isManager
              ? 'Approvals, decisions and questions from the team. Reply, then approve, decline or mark it answered.'
              : 'Ask the directors for an approval, a decision or an answer. Only you, the directors and the owner can see your requests.'}
          </p>
        </div>
        <Button
          onClick={() => { setPrefill(undefined); setNewOpen(true); }}
          className="bg-emerald-600 hover:bg-emerald-500 text-white shrink-0"
        >
          <Plus className="w-4 h-4 mr-2" /> New request
        </Button>
      </div>

      <Tabs value={view} onValueChange={(v) => { setView(v as 'open' | 'closed'); setSelectedId(null); }}>
        <TabsList className="bg-gray-900 border border-gray-800">
          <TabsTrigger value="open" className="px-4 text-gray-400 data-[state=active]:bg-gray-700 data-[state=active]:text-white">Waiting</TabsTrigger>
          <TabsTrigger value="closed" className="px-4 text-gray-400 data-[state=active]:bg-gray-700 data-[state=active]:text-white">Closed</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,380px)_minmax(0,1fr)]">
        {/* List */}
        <Card className="bg-gray-900/50 border-gray-800 self-start">
          <CardContent className="p-2">
            {loading ? (
              <div className="space-y-2 p-2">
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 bg-gray-800/50" />)}
              </div>
            ) : rows.length === 0 ? (
              <div className="py-12 text-center space-y-2">
                <Inbox className="w-6 h-6 text-gray-600 mx-auto" />
                <p className="text-sm text-gray-500">{emptyText}</p>
              </div>
            ) : (
              <ul className="space-y-1">
                {rows.map((r) => {
                  const active = r.id === selectedId;
                  return (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => setSelectedId(r.id)}
                        aria-label={`Open request ${r.subject}`}
                        className={`w-full text-left rounded-lg px-3 py-2.5 transition-colors ${
                          active ? 'bg-cyan-500/10 ring-1 ring-cyan-500/30' : 'hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {r.unread && <span className="w-2 h-2 rounded-full bg-cyan-400 shrink-0" aria-label="Unread" />}
                          <span className={`text-sm truncate ${r.unread ? 'text-white font-semibold' : 'text-gray-200'}`}>
                            {r.subject}
                          </span>
                        </div>
                        <div className="mt-1 flex items-center gap-2 text-[11px] text-gray-500">
                          <span>{KIND_LABELS[r.kind] ?? r.kind}</span>
                          <span>·</span>
                          <span className="truncate">
                            {isManager && !r.is_mine ? r.created_by.display_name : r.assigned_to ? `To ${r.assigned_to.display_name}` : 'To any director'}
                          </span>
                          <span>·</span>
                          <span className="shrink-0">{ago(r.last_activity_at)}</span>
                        </div>
                        {view === 'closed' && (
                          <Badge variant="outline" className={`mt-1.5 text-[10px] ${STATUS[r.status].className}`}>
                            {STATUS[r.status].label}
                          </Badge>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Detail */}
        <Card className="bg-gray-900/50 border-gray-800 min-h-[320px]">
          <CardContent className="p-5">
            {!selectedId ? (
              <div className="h-full min-h-[260px] flex flex-col items-center justify-center text-center gap-2">
                <MessageSquare className="w-6 h-6 text-gray-600" />
                <p className="text-sm text-gray-500">Pick a request to read the conversation.</p>
              </div>
            ) : detailLoading || !req ? (
              <div className="space-y-3">
                <Skeleton className="h-6 w-2/3 bg-gray-800/50" />
                <Skeleton className="h-4 w-1/3 bg-gray-800/50" />
                <Skeleton className="h-24 w-full bg-gray-800/50" />
              </div>
            ) : (
              <div className="space-y-5">
                <div className="space-y-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className="text-lg font-semibold text-white">{req.subject}</h2>
                    <Badge variant="outline" className={`text-[10px] ${STATUS[req.status].className}`}>
                      {STATUS[req.status].label}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500">
                    {KIND_LABELS[req.kind] ?? req.kind} from {personLabel(req.created_by)}
                    {' · '}
                    {req.assigned_to ? `to ${req.assigned_to.display_name}` : 'to any director'}
                    {' · '}
                    {ago(req.created_at)}
                  </p>
                  {req.page_path && (
                    <p className="text-xs text-gray-500">
                      Sent from{' '}
                      <Link href={req.page_path} className="font-mono text-cyan-400 hover:underline">{req.page_path}</Link>
                    </p>
                  )}
                </div>

                <div className="rounded-lg bg-gray-800/40 border border-gray-800 p-4">
                  <p className="text-sm text-gray-200 whitespace-pre-wrap">{req.body}</p>
                </div>

                {detail.messages.length > 0 && (
                  <ol className="space-y-3">
                    {detail.messages.map((m) => (
                      <li key={m.id} className={`flex ${m.is_mine ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[85%] rounded-lg px-3.5 py-2.5 ${
                          m.is_mine ? 'bg-emerald-500/10 border border-emerald-500/20' : 'bg-gray-800/60 border border-gray-700/60'
                        }`}>
                          <p className="text-[11px] text-gray-400 mb-1">
                            {m.is_mine ? 'You' : personLabel(m.author)}
                            {m.status_change && <span className="text-gray-300"> {STATUS_VERB[m.status_change]}</span>}
                            {' · '}
                            {ago(m.created_at)}
                          </p>
                          {!(m.status_change && m.body === '(no note)') && (
                            <p className="text-sm text-gray-100 whitespace-pre-wrap">{m.body}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                )}

                {req.status !== 'withdrawn' && (
                  <div className="space-y-3 border-t border-gray-800 pt-4">
                    <Textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      maxLength={4000}
                      rows={3}
                      placeholder={canDecide && req.status === 'open'
                        ? 'Reply, or add a note and then approve, decline or mark answered.'
                        : 'Write a reply.'}
                      className="bg-gray-800 border-gray-700 text-white"
                      aria-label="Reply"
                    />
                    <div className="flex flex-wrap items-center gap-2">
                      <Button
                        onClick={sendReply}
                        disabled={!reply.trim() || busy != null}
                        variant="outline"
                        className="border-gray-700 text-gray-200 hover:text-white"
                      >
                        {busy === 'reply' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CornerDownLeft className="w-4 h-4 mr-2" />}
                        Reply
                      </Button>

                      {canDecide && req.status === 'open' && (
                        <>
                          <Button onClick={() => changeStatus('approved')} disabled={busy != null} className="bg-emerald-600 hover:bg-emerald-500 text-white">
                            {busy === 'approved' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                            Approve
                          </Button>
                          <Button onClick={() => changeStatus('declined')} disabled={busy != null} className="bg-rose-600 hover:bg-rose-500 text-white">
                            {busy === 'declined' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <X className="w-4 h-4 mr-2" />}
                            Decline
                          </Button>
                          <Button onClick={() => changeStatus('answered')} disabled={busy != null} variant="outline" className="border-cyan-700 text-cyan-300 hover:text-cyan-200">
                            {busy === 'answered' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <MessageSquare className="w-4 h-4 mr-2" />}
                            Mark answered
                          </Button>
                        </>
                      )}

                      {isManager && req.status !== 'open' && (
                        <Button onClick={() => changeStatus('open')} disabled={busy != null} variant="ghost" className="text-gray-400 hover:text-white">
                          {busy === 'open' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RotateCcw className="w-4 h-4 mr-2" />}
                          Reopen
                        </Button>
                      )}

                      {req.is_mine && req.status === 'open' && (
                        <Button onClick={() => changeStatus('withdrawn')} disabled={busy != null} variant="ghost" className="text-gray-400 hover:text-white ml-auto">
                          {busy === 'withdrawn' ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Undo2 className="w-4 h-4 mr-2" />}
                          Withdraw
                        </Button>
                      )}
                    </div>
                    {canDecide && req.status === 'open' && (
                      <p className="text-[11px] text-gray-500">
                        Anything typed above is sent with your decision as the note.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
